import { useState } from "react";
import type { BindingCtx, DomainId, DomainRead } from "../../../state/editor/types";
import { useEditorStore, usePoll } from "../../../state/editor/useEditor";
import { ROW_HELP } from "../sections/help";
import { CUSTOM_TERMS } from "../sections/searchIndex.gen";
import { useRowEdit } from "./history";
import { rangeText, splitUnit } from "./rowMeta";
import type {
  Binding,
  ButtonRow,
  CustomRow,
  MonitorRow,
  NoteRow,
  RowDef,
  TextActionRow,
} from "./schema";
import { useSectionInfo } from "./sectionContext";
import {
  CompactCheck,
  CompactColor,
  CompactNumber,
  CompactSelect,
  CompactSlider,
  CompactText,
  InertChip,
  hiddenInert,
  isDevLabel,
  Marked,
  MiniBtn,
  RecordOff,
  Row,
  RowInset,
  TextActionInput,
  type RowExtras,
} from "./rows";

/** Copy / Paste value between rows of the same kind (right-click menu). */
let copied: { kind: RowDef["kind"]; value: unknown } | null = null;

type Bound = Extract<RowDef, { binding: unknown }>;

export function RowRenderer({ row, read, ctx }: { row: RowDef; read: DomainRead; ctx: BindingCtx }) {
  const edit = useRowEdit();
  const info = useSectionInfo();
  if (row.visible && !row.visible(read, ctx)) return null;
  // A second control over a value another row already owns: clutter, and
  // the way two copies drift apart. Kept, but only with "Show experimental".
  if (((row.inert && hiddenInert(row.inert)) || isDevLabel(row.label)) && info && !info.showExperimental) return null;
  // A string verdict means "disabled, and here is why" — the reason is printed
  // under the row by DisabledWrap.
  const verdict = row.disabled ? row.disabled(read, ctx) : false;
  const disabled = verdict !== false;
  const reason = typeof verdict === "string" ? verdict : null;

  const { text: label, unit } = splitUnit(row.label ?? "", "unit" in row ? row.unit : undefined);
  const path = info ? `${info.tabId}.${info.sectionId}.${row.id}` : undefined;
  const bound = "binding" in row ? (row as Bound) : null;
  const binding = bound ? (bound.binding as Binding<unknown>) : null;
  const stepLabel = info ? `${info.title} · ${label}` : label;
  /** Write through the row's own binding, recorded for Undo. Gestures merge. */
  const write = (v: unknown, gesture?: number) => {
    if (binding) edit(ctx, binding, v, stepLabel, gesture != null && path ? `${path}#${gesture}` : undefined);
  };

  const pinned = !!(path && info?.pins.has(path));
  const rowHelp = info ? ROW_HELP[`${info.sectionId}.${row.id}`] : undefined;
  const extras: RowExtras = {
    help: rowHelp?.text,
    defaultText: rowHelp?.default,
    range: rangeText(row, unit),
    unit: row.kind === "slider" || row.kind === "number" ? unit : undefined,
    path,
    mark: info?.mark,
    pinned,
    onPin: path && info ? () => info.togglePin(path) : undefined,
    menu:
      path && info
        ? [
            { label: pinned ? "Unpin" : "Pin to Pinned", run: () => info.togglePin(path) },
            ...(binding && bound
              ? [
                  {
                    label: "Copy value",
                    run: () => {
                      const value = binding.get(read, ctx);
                      copied = { kind: bound.kind, value };
                      void navigator.clipboard?.writeText(typeof value === "string" ? value : JSON.stringify(value)).catch(() => undefined);
                    },
                  },
                  {
                    label: "Paste value",
                    disabled: !copied || copied.kind !== bound.kind || disabled,
                    run: () => { if (copied && copied.kind === bound.kind) write(copied.value); },
                  },
                ]
              : []),
          ]
        : undefined,
  };
  if ((row.kind === "slider" || row.kind === "number") && binding && !disabled) {
    extras.scrub = {
      value: binding.get(read, ctx) as number,
      min: row.min,
      max: row.max,
      step: row.step ?? 1,
      onScrub: (v, g) => write(v, g),
    };
  }

  const wrap = (children: React.ReactNode, opts?: { stacked?: boolean; noLabel?: boolean; wide?: boolean }) => (
    <DisabledWrap disabled={disabled} reason={reason}>
      <Row label={opts?.noLabel ? undefined : label} hint={row.hint} stacked={opts?.stacked} inert={row.inert} extras={extras} wide={opts?.wide}>
        <RecordOff>{children}</RecordOff>
      </Row>
    </DisabledWrap>
  );

  switch (row.kind) {
    case "checkbox": {
      const v = row.binding.get(read, ctx);
      return wrap(<CompactCheck checked={v} onChange={(nv) => write(nv)} />, { wide: true });
    }
    case "slider": {
      const v = row.binding.get(read, ctx);
      return wrap(
        <CompactSlider
          value={v}
          min={row.min}
          max={row.max}
          step={row.step}
          unit={unit}
          format={row.format}
          onChange={(nv, g) => write(nv, g ?? 0)}
        />,
      );
    }
    case "number": {
      const v = row.binding.get(read, ctx);
      return wrap(
        <CompactNumber value={v} min={row.min} max={row.max} step={row.step} onChange={(nv, g) => write(nv, g ?? 0)} />,
      );
    }
    case "color": {
      const v = row.binding.get(read, ctx);
      return wrap(
        <CompactColor
          value={v ?? "#ffffff"}
          onChange={(hex) => write(hex, 0)}
          clearable={row.clearable}
          onClear={() => write(null)}
        />,
      );
    }
    case "vec3": {
      const [x, y, z] = row.binding.get(read, ctx);
      const axes = row.axes ?? ["X", "Y", "Z"];
      const emit = (nx: number, ny: number, nz: number, g?: number) => write([nx, ny, nz], g ?? 0);
      return wrap(
        <div className="flex min-w-0 flex-1 items-center gap-1">
          {[x, y, z].map((val, i) => (
            <label key={i} className="flex min-w-0 flex-1 items-center gap-1">
              <span className="text-[11px] text-ed-text-3">{axes[i]}</span>
              <CompactNumber
                value={val}
                min={row.min}
                max={row.max}
                step={row.step}
                width={48}
                onChange={(nv, g) => emit(i === 0 ? nv : x, i === 1 ? nv : y, i === 2 ? nv : z, g)}
              />
            </label>
          ))}
        </div>,
        { stacked: true },
      );
    }
    case "dropdown": {
      const v = row.binding.get(read, ctx);
      const opts = typeof row.options === "function" ? row.options(read, ctx) : row.options;
      return wrap(<CompactSelect value={v} options={opts} onChange={(nv) => write(nv)} />);
    }
    case "text": {
      const v = row.binding.get(read, ctx);
      return wrap(<CompactText value={v} placeholder={row.hint} onChange={(nv) => write(nv)} />);
    }
    case "textAction":
      return <TextActionRowView row={row} ctx={ctx} disabled={disabled} reason={reason} extras={extras} label={label} />;
    case "button":
      return <ButtonRowView row={row} ctx={ctx} disabled={disabled} reason={reason} extras={extras} label={label} />;
    case "monitor":
      return <MonitorRowView row={row} read={read} ctx={ctx} extras={extras} label={label} />;
    case "note":
      return <NoteRowView row={row} read={read} ctx={ctx} />;
    case "custom":
      if (row.inline && label) {
        const C = row.component;
        return wrap(<div className="min-w-0 flex-1"><C ctx={ctx} read={read} /></div>);
      }
      return <CustomRowView row={row} read={read} ctx={ctx} mark={info?.mark} />;
    default:
      return null;
  }
}

/**
 * The greyed-out shell for a disabled row, plus its reason.
 *
 * The reason has to live OUTSIDE the greyed div: `pointer-events-none` means a
 * disabled row is not a hit target, so the `hint` tooltip that used to carry the
 * explanation could never fire — the user saw an undraggable control with no
 * word of why. It is also outside `opacity-40`, or the one line explaining the
 * row would be the faintest text on screen.
 */
function DisabledWrap({
  disabled,
  reason,
  children,
}: {
  disabled: boolean;
  reason: string | null;
  children: React.ReactNode;
}) {
  return (
    <>
      <div className={disabled ? "pointer-events-none opacity-40" : ""}>{children}</div>
      {disabled && reason ? <p className="px-3 pb-1.5 text-[11px] leading-snug text-ed-text-3">{reason}</p> : null}
    </>
  );
}


/** Re-read the domains an action row declares once its work settles. Runs on
 *  failure too: a half-applied change still moved the viewer, and leaving the
 *  panel showing the pre-click slice is how a control looks broken. */
function runThenRefresh(
  work: void | Promise<unknown>,
  domains: DomainId[] | undefined,
  store: ReturnType<typeof useEditorStore>,
): Promise<unknown> | void {
  if (!domains?.length) return work;
  const after = () => { for (const d of domains) void store.refresh(d); };
  return Promise.resolve(work).then(
    (v) => { after(); return v; },
    (e) => { after(); throw e; },
  );
}

function TextActionRowView({
  row,
  ctx,
  disabled,
  reason,
  extras,
  label,
}: {
  row: TextActionRow;
  ctx: BindingCtx;
  disabled: boolean;
  reason: string | null;
  extras: RowExtras;
  label: string;
}) {
  const store = useEditorStore();
  return (
    <DisabledWrap disabled={disabled} reason={reason}>
      <Row label={label} hint={row.hint} stacked inert={row.inert} extras={extras}>
        {/* Return the promise rather than discarding it — TextActionInput uses
            it to hold a spinner, keep the URL on failure, and swallow what would
            otherwise be an unhandled rejection. */}
        <TextActionInput placeholder={row.placeholder} action={row.action} onSubmit={(v) => runThenRefresh(row.run(v, ctx), row.refreshDomains, store)} />
      </Row>
    </DisabledWrap>
  );
}

function ButtonRowView({
  row,
  ctx,
  disabled,
  reason,
  extras,
  label,
}: {
  row: ButtonRow;
  ctx: BindingCtx;
  disabled: boolean;
  reason: string | null;
  extras: RowExtras;
  label: string;
}) {
  // Per-button, not per-row: a single shared flag put "…" on every button in
  // the row when any one of them ran, and left them all clickable besides.
  const [busyIdx, setBusyIdx] = useState<number | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const store = useEditorStore();

  // `refreshDomains` was declared on ButtonRow and never read here, so it was a
  // silent no-op on every button: "Load Metal HDR File" changed metalEnvSource
  // and the rows that gate on it kept rendering the pre-click slice. Refreshed
  // on BOTH paths — a synchronous action returns undefined and used to leave
  // through the early return below without ever refreshing.
  const refresh = () => {
    for (const d of row.refreshDomains ?? []) void store.refresh(d);
  };

  const run = (i: number, fn: (c: BindingCtx) => void | Promise<unknown>) => {
    if (busyIdx !== null) return;
    setFailed(null);
    let res: void | Promise<unknown>;
    try {
      res = fn(ctx);
    } catch (e) {
      setFailed(e instanceof Error ? e.message : "Action failed.");
      refresh();
      return;
    }
    if (!res || typeof (res as Promise<unknown>).then !== "function") { refresh(); return; }
    setBusyIdx(i);
    (res as Promise<unknown>).then(
      () => { setBusyIdx(null); refresh(); },
      (e) => {
        setBusyIdx(null);
        // Without this the rejection was unhandled and the row looked inert.
        setFailed(e instanceof Error ? e.message : "Action failed.");
        // Refreshed on failure too: a half-applied action still moved the
        // viewer, and showing the pre-click slice is how a control looks broken.
        refresh();
      },
    );
  };

  return (
    <DisabledWrap disabled={disabled} reason={reason}>
      <Row label={label || undefined} hint={row.hint} stacked={!label} inert={row.inert} extras={extras}>
        <div className="flex min-w-0 flex-1 flex-col items-end gap-1">
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            {row.buttons.map((b, i) => (
              <MiniBtn
                key={i}
                icon={b.icon}
                primary={b.variant === "primary"}
                busy={busyIdx === i}
                disabled={busyIdx !== null && busyIdx !== i}
                onClick={() => run(i, b.run)}
              >
                {b.label}
              </MiniBtn>
            ))}
          </div>
          {failed ? <span className="text-[11px] text-ed-red">{failed}</span> : null}
        </div>
      </Row>
    </DisabledWrap>
  );
}

function MonitorRowView({ row, read, ctx, extras, label }: { row: MonitorRow; read: DomainRead; ctx: BindingCtx; extras: RowExtras; label: string }) {
  usePoll(row.domain, row.pollMs ?? 400, !!row.pollMs);
  return (
    <Row label={label} hint={row.hint} inert={row.inert} extras={extras}>
      <span className="text-[12px] tabular-nums text-ed-text">{row.read(read, ctx)}</span>
    </Row>
  );
}

function NoteRowView({ row, read, ctx }: { row: NoteRow; read: DomainRead; ctx: BindingCtx }) {
  const text = typeof row.text === "function" ? row.text(read, ctx) : row.text;
  if (!text) return null;
  return (
    <p className="px-3 py-1.5 text-[12px] leading-snug text-ed-text-3">
      {row.inert ? <InertChip tag={row.inert} /> : null} {text}
    </p>
  );
}

function CustomRowView({ row, read, ctx, mark }: { row: CustomRow; read: DomainRead; ctx: BindingCtx; mark?: string[] }) {
  const C = row.component;
  // A custom block draws its own labels, so a search hit inside it cannot be
  // marked in place; name what matched above it instead.
  const info = useSectionInfo();
  const terms = [...(row.keywords ? row.keywords.split(/[,;·]/) : []), ...(info ? CUSTOM_TERMS[`${info.sectionId}.${row.id}`] ?? [] : [])];
  const hit = mark?.length && terms.length ? matchedTerms(terms, mark) : [];
  // Inner <Row>s go flush (RowInset): the block's own px-3 already insets
  // them, and doubling it put custom rows 12px right of every bound row.
  return (
    <div className="px-3 py-1">
      {row.inert ? <div className="pb-1"><InertChip tag={row.inert} /></div> : null}
      {hit.length ? (
        <p className="pb-1 text-[11px] text-ed-text-3">
          Matches: <Marked text={hit.join(", ")} terms={mark} />
        </p>
      ) : null}
      <RowInset.Provider value="flush">
        <C ctx={ctx} read={read} />
      </RowInset.Provider>
    </div>
  );
}

/** The labels (keywords, or what the custom block renders) containing a search term. */
function matchedTerms(labels: string[], terms: string[]): string[] {
  const t = terms.map((x) => x.toLowerCase());
  return labels
    .map((k) => k.trim())
    .filter((k) => k && t.some((x) => k.toLowerCase().includes(x)))
    .slice(0, 4);
}
